export type AppEnvKey = "PROD" | "DEV93" | "DEV98" | "MOST";

export interface AppEnvironment {
  key: AppEnvKey;
  label: string;
  baseUrl: string;
}

export const APP_ENVIRONMENTS: Record<AppEnvKey, AppEnvironment> = {
  PROD: {
    key: "PROD",
    label: "PROD",
    baseUrl: import.meta.env.VITE_APP_PROD_BASE_URL || "/api-prod",
  },
  DEV93: {
    key: "DEV93",
    label: "DEV93",
    baseUrl: import.meta.env.VITE_APP_DEV93_BASE_URL || "/api-dev-93",
  },
  DEV98: {
    key: "DEV98",
    label: "DEV98",
    baseUrl: import.meta.env.VITE_APP_DEV98_BASE_URL || "/api-dev-98",
  },
  MOST: {
    key: "MOST",
    label: "MOST",
    baseUrl: import.meta.env.VITE_APP_MOST_BASE_URL || "/api-most",
  },
};

export const APP_ENVIRONMENT_LIST: AppEnvironment[] = Object.values(APP_ENVIRONMENTS);

// A production build (`build:prod`, --mode production) is hard-pinned to PROD and
// ignores every override: the switcher UI is dropped, a stored APP.ENV is ignored,
// and VITE_APP_ENV cannot redirect it. Vite replaces this statically, so all the
// non-production branches below are eliminated from the bundle rather than merely
// skipped at runtime.
// MODE, not PROD: `vite build` forces NODE_ENV=production (and so `import.meta.env.PROD`)
// for every build, including `--mode development`. MODE is what the --mode flag actually
// controls, so this is the only thing that separates `build` from `build:prod`.
const IS_PRODUCTION_BUILD = import.meta.env.MODE === "production";

export const ENV_SWITCHER_ENABLED = !IS_PRODUCTION_BUILD;

const STORAGE_KEY = "APP.ENV";

const isAppEnvKey = (value: unknown): value is AppEnvKey =>
  typeof value === "string" && Object.prototype.hasOwnProperty.call(APP_ENVIRONMENTS, value);

// Never trust VITE_APP_ENV in a production build: a stale `VITE_APP_ENV=MOST` in
// someone's local .env would otherwise ship a kiosk pointed at UAT.
const buildEnvKey: AppEnvKey = IS_PRODUCTION_BUILD
  ? "PROD"
  : isAppEnvKey(import.meta.env.VITE_APP_ENV)
    ? import.meta.env.VITE_APP_ENV
    : "PROD";

const storedEnvKey = (): AppEnvKey | null => {
  if (IS_PRODUCTION_BUILD) return null;
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null");
    return isAppEnvKey(stored) ? stored : null;
  } catch {
    return null;
  }
};

// Resolved once on startup: switching writes the choice and reloads, so every
// module (e.g. the axios client) stays on a single consistent env.
export const currentEnvKey: AppEnvKey = storedEnvKey() ?? buildEnvKey;
export const currentEnv: AppEnvironment = APP_ENVIRONMENTS[currentEnvKey];

export const setCurrentEnv = (key: AppEnvKey) => {
  if (!ENV_SWITCHER_ENABLED || !isAppEnvKey(key) || key === currentEnvKey) return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(key));
  window.location.reload();
};
